import { useEffect, useRef, useState } from "react";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import {
  getZohoConfigURL,
  saveZohoConfigURL,
  updateZohoConfigURL,
  deleteZohoConfigURL,
  testZohoConnectionURL,
  refreshZohoTokenURL,
} from "../../../utils/urls";
import ZohoItemMapping from "./ZohoItemMapping";
import ZohoUtilitySync, { ZOHO_UTILITY_SYNC_FACILITY_ID } from "./ZohoUtilitySync";

const maskMiddle = (str, start = 4, end = 4, maskChar = "*") => {
  if (!str || typeof str !== "string") return "";
  const len = str.length;
  if (len <= start + end) return maskChar.repeat(Math.max(0, len));
  return `${str.slice(0, start)}${maskChar.repeat(len - start - end)}${str.slice(len - end)}`;
};

const showMaskedOrNA = (v) => {
  const s = typeof v === "string" ? v : "";
  return s ? maskMiddle(s) : "—";
};

// Everything to do with Zoho Books lives here: connection and credentials, item
// mapping, and (for Northlands) the power & water sync.
const ZohoIntegration = ({ facilityId }) => {
  const [zohoConfig, setZohoConfig] = useState(null);
  const [zohoLoading, setZohoLoading] = useState(true);
  const [showZohoForm, setShowZohoForm] = useState(false);
  const [zohoFormData, setZohoFormData] = useState({
    clientId: "",
    clientSecret: "",
    refreshToken: "",
    organizationId: "",
  });
  const [zohoSaving, setZohoSaving] = useState(false);
  const [zohoRefreshing, setZohoRefreshing] = useState(false);
  const zohoRequestVersion = useRef(0);
  const [activeTab, setActiveTab] = useState("connection");

  useEffect(() => {
    const requestVersion = ++zohoRequestVersion.current;

    setZohoConfig(null);
    setShowZohoForm(false);
    setZohoFormData({ clientId: "", clientSecret: "", refreshToken: "", organizationId: "" });

    if (facilityId) {
      loadZohoConfig(facilityId, requestVersion);
    } else {
      setZohoLoading(false);
    }

    return () => {
      // Invalidate any in-flight Zoho response when the facility changes/unmounts.
      zohoRequestVersion.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);

  // ==================== ZOHO BOOKS FUNCTIONS ====================

  const loadZohoConfig = async (targetFacilityId = facilityId, requestVersion = zohoRequestVersion.current) => {
    if (!targetFacilityId) {
      setZohoConfig(null);
      setZohoLoading(false);
      return;
    }

    try {
      setZohoLoading(true);
      const response = await makeRequest2(getZohoConfigURL(targetFacilityId), "GET");

      // Do not let a slower request for Facility A overwrite Facility B.
      if (requestVersion !== zohoRequestVersion.current || targetFacilityId !== facilityId) return;

      if (response?.success && response.data?.success) {
        setZohoConfig(response.data.data?.config || null);
      } else {
        setZohoConfig(null);
      }
    } catch (error) {
      if (requestVersion === zohoRequestVersion.current && targetFacilityId === facilityId) {
        console.error("Error loading Zoho config:", error);
        setZohoConfig(null);
      }
    } finally {
      if (requestVersion === zohoRequestVersion.current && targetFacilityId === facilityId) {
        setZohoLoading(false);
      }
    }
  };

  const handleZohoInputChange = (e) => {
    const { name, value } = e.target;
    setZohoFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveZohoConfig = async (e) => {
    e.preventDefault();
    if (!facilityId || zohoSaving) return;

    setZohoSaving(true);
    try {
      const isUpdate = Boolean(zohoConfig);
      const url = isUpdate
        ? updateZohoConfigURL(facilityId)
        : saveZohoConfigURL(facilityId);

      // On update, blank secret fields intentionally mean "keep the current secret".
      const payload = {
        clientId: zohoFormData.clientId.trim(),
        organizationId: zohoFormData.organizationId.trim(),
        ...(isUpdate || zohoFormData.clientSecret ? { clientSecret: zohoFormData.clientSecret } : {}),
        ...(isUpdate || zohoFormData.refreshToken ? { refreshToken: zohoFormData.refreshToken } : {}),
        ...(isUpdate ? { isActive: true } : {}),
      };

      const response = await makeRequest2(url, isUpdate ? "PUT" : "POST", payload);

      if (response?.success && response.data?.success) {
        toastify(
          "success",
          response.data.message || "Zoho Books integration configured successfully",
        );
        setShowZohoForm(false);
        setZohoFormData({ clientId: "", clientSecret: "", refreshToken: "", organizationId: "" });
        await loadZohoConfig(facilityId, zohoRequestVersion.current);
      } else {
        toastify(
          "error",
          response?.data?.error || response?.data?.message || response?.error || "Failed to save Zoho configuration",
        );
      }
    } catch (error) {
      console.error("Error saving Zoho config:", error);
      toastify("error", error.message || "Failed to save Zoho configuration");
    } finally {
      setZohoSaving(false);
    }
  };

  const handleTestZohoConnection = async () => {
    if (!zohoConfig) {
      toastify("error", "Please save your Zoho configuration first before testing the connection");
      return;
    }
    try {
      const url = testZohoConnectionURL(facilityId);
      const response = await makeRequest2(url, "POST");

      if (response && response.success) {
        const backendData = response.data;
        if (backendData && backendData.success) {
          toastify("success", backendData.message || "Connection test successful");
        } else {
          toastify("error", backendData?.error || backendData?.message || "Connection test failed");
          if (backendData?.needsRefresh) await loadZohoConfig(facilityId, zohoRequestVersion.current);
        }
      } else {
        toastify("error", response?.error || "Connection test failed");
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      toastify("error", "Failed to test connection");
    }
  };

  const handleDisconnectZoho = async () => {
    if (!facilityId || !zohoConfig) return;
    if (!window.confirm("Are you sure you want to disconnect Zoho Books integration for this facility?")) return;

    try {
      const response = await makeRequest2(deleteZohoConfigURL(facilityId), "DELETE");

      if (response?.success && response.data?.success) {
        toastify("success", response.data.message || "Zoho Books integration disconnected");
        setShowZohoForm(false);
        await loadZohoConfig(facilityId, zohoRequestVersion.current);
      } else {
        toastify(
          "error",
          response?.data?.error || response?.data?.message || response?.error || "Failed to disconnect",
        );
      }
    } catch (error) {
      console.error("Error disconnecting Zoho:", error);
      toastify("error", error.message || "Failed to disconnect Zoho integration");
    }
  };

  const handleEditZoho = async () => {
    try {
      setZohoLoading(true);
      const url = `${getZohoConfigURL(facilityId)}?unmask=true`;
      const response = await makeRequest2(url, "GET");

      if (response && response.success) {
        const backendData = response.data;
        if (backendData && backendData.success && backendData.data) {
          const config = backendData.data.config;
          setZohoFormData({
            clientId:       config.clientId       || "",
            clientSecret:   config.clientSecret   || "",
            refreshToken:   config.refreshToken   || "",
            organizationId: config.organizationId || "",
          });
          setShowZohoForm(true);
        } else {
          toastify(
            "error",
            backendData?.error || backendData?.message || "Failed to fetch credentials for editing",
          );
        }
      } else {
        toastify("error", response?.error || "Failed to fetch credentials");
      }
      setZohoLoading(false);
    } catch (error) {
      console.error("Error fetching credentials for edit:", error);
      toastify("error", error.message || "Failed to fetch credentials");
      setZohoLoading(false);
    }
  };

  const handleRefreshZohoToken = async () => {
    if (!facilityId || !zohoConfig) return;
    try {
      setZohoRefreshing(true);
      const response = await makeRequest2(refreshZohoTokenURL(facilityId), "POST");
      if (response?.success && response.data?.success) {
        toastify("success", response.data.message || "Zoho credentials refreshed");
      } else {
        toastify("error", response?.data?.error || response?.error || "Could not refresh Zoho credentials");
      }
      await loadZohoConfig(facilityId, zohoRequestVersion.current);
    } catch (error) {
      toastify("error", error.message || "Could not refresh Zoho credentials");
    } finally {
      setZohoRefreshing(false);
    }
  };

  const connected = !!zohoConfig?.isActive;
  const isNorthlands = String(facilityId) === ZOHO_UTILITY_SYNC_FACILITY_ID;
  const tabs = [
    { id: "connection", label: "Connection", icon: "ti-plug-connected" },
    { id: "mapping", label: "Item mapping", icon: "ti-arrows-exchange", needsConnection: true },
    ...(isNorthlands ? [{ id: "sync", label: "Power & Water sync", icon: "ti-cloud-upload", needsConnection: true }] : []),
  ];
  const tab = tabs.find((t) => t.id === activeTab && (!t.needsConnection || connected)) ? activeTab : "connection";

  return (
    <div>
      <ul className="nav nav-pills gap-2 mb-4">
        {tabs.map((t) => {
          const disabled = t.needsConnection && !connected;
          return (
            <li className="nav-item" key={t.id}>
              <button
                className={`nav-link px-3 ${tab === t.id ? "active" : "bg-light text-dark"}`}
                disabled={disabled}
                title={disabled ? "Connect Zoho Books first" : ""}
                onClick={() => setActiveTab(t.id)}
              >
                <i className={`ti ${t.icon} me-1`}></i>
                {t.label}
              </button>
            </li>
          );
        })}
      </ul>

      {tab === "connection" && (
        <div className="row">
          <div className="col-xl-7 col-lg-8 col-md-12 mb-4">
            <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center mb-4">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0 me-3 rounded-3"
                  style={{ width: 52, height: 52, background: "rgba(40,167,69,0.12)" }}
                >
                  <i className="ti ti-books" style={{ fontSize: 28, color: "#28a745" }}></i>
                </div>
                <div className="flex-grow-1">
                  <h5 className="mb-0">Zoho Books</h5>
                  <small className="text-muted">Sync invoices and payments with Zoho Books accounting</small>
                </div>
                {zohoConfig && !showZohoForm && (
                  <span
                    className={`badge rounded-pill px-3 py-2 ${
                      zohoConfig.isActive && zohoConfig.connectionStatus === "connected"
                        ? "bg-success-subtle text-success"
                        : zohoConfig.connectionStatus === "error"
                          ? "bg-danger-subtle text-danger"
                          : "bg-warning-subtle text-warning"
                    }`}
                  >
                    <i className="ti ti-point-filled me-1"></i>
                    {zohoConfig.isActive && zohoConfig.connectionStatus === "connected"
                      ? "Connected"
                      : zohoConfig.connectionStatus === "error"
                        ? "Connection error"
                        : zohoConfig.isActive
                          ? "Pending"
                          : "Disconnected"}
                  </span>
                )}
                {!zohoConfig && !showZohoForm && (
                  <span className="badge rounded-pill px-3 py-2 bg-light text-dark border">Not connected</span>
                )}
              </div>

              {zohoLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Connected State */}
                  {zohoConfig && !showZohoForm && (
                    <div>
                      {!(zohoConfig.isActive && zohoConfig.connectionStatus === "connected") && (
                        <div
                          className={`alert ${zohoConfig.connectionStatus === "error" ? "alert-danger" : "alert-warning"} d-flex align-items-start py-2 mb-3`}
                          role="alert"
                        >
                          <i className="ti ti-alert-triangle me-2 mt-1"></i>
                          <span>{zohoConfig.connectionError || `Zoho Books status: ${zohoConfig.connectionStatus || "disconnected"}`}</span>
                        </div>
                      )}

                      <div className="row g-2 mb-3">
                        {[
                          { icon: "ti-building", label: "Organization", value: zohoConfig.organizationId },
                          {
                            icon: "ti-circle-check",
                            label: "Status",
                            value: (
                              <span className={`badge ${zohoConfig.isActive ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}>
                                {zohoConfig.isActive ? "Active" : "Inactive"}
                              </span>
                            ),
                          },
                          {
                            icon: "ti-clock",
                            label: "Last synced",
                            value: zohoConfig.lastSyncAt ? new Date(zohoConfig.lastSyncAt).toLocaleString() : "Never",
                          },
                          { icon: "ti-file-invoice", label: "Invoices synced", value: zohoConfig.syncStats?.invoicesSynced || 0 },
                        ].map((t) => (
                          <div className="col-6" key={t.label}>
                            <div className="h-100 rounded-3 p-3" style={{ background: "var(--bs-light, #f6f7f9)" }}>
                              <div className="text-muted small mb-1">
                                <i className={`ti ${t.icon} me-1`}></i>
                                {t.label}
                              </div>
                              <div className="fw-semibold text-break">{t.value}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="rounded-3 border p-3 mb-3">
                        <div className="text-muted small mb-2">
                          <i className="ti ti-key me-1"></i>Credentials
                        </div>
                        {[
                          ["Client ID", zohoConfig?.clientId],
                          ["Client secret", zohoConfig?.clientSecret],
                          ["Refresh token", zohoConfig?.refreshToken],
                        ].map(([name, val]) => (
                          <div className="d-flex justify-content-between align-items-center small py-1" key={name}>
                            <span className="text-muted">{name}</span>
                            <code className="text-body">{showMaskedOrNA(val)}</code>
                          </div>
                        ))}
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        <button className="btn btn-light flex-fill" onClick={handleEditZoho}>
                          <i className="ti ti-edit me-1"></i>Edit
                        </button>
                        <button className="btn btn-primary flex-fill" onClick={handleTestZohoConnection} disabled={!zohoConfig.isActive}>
                          <i className="ti ti-plug-connected me-1"></i>Test connection
                        </button>
                        <button className="btn btn-outline-primary flex-fill" onClick={handleRefreshZohoToken} disabled={!zohoConfig.isActive || zohoRefreshing}>
                          {zohoRefreshing ? <span className="spinner-border spinner-border-sm me-1" role="status"></span> : <i className="ti ti-key me-1"></i>}
                          Refresh credentials
                        </button>
                        <button className="btn btn-outline-danger flex-fill" onClick={handleDisconnectZoho} disabled={!zohoConfig.isActive}>
                          <i className="ti ti-plug-off me-1"></i>Disconnect
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Not Connected / Form */}
                  {(!zohoConfig || showZohoForm) && (
                    <div>
                      {showZohoForm && zohoConfig && (
                        <div className="alert alert-info mb-3" role="alert">
                          <i className="ti ti-info-circle me-2"></i>
                          Update your Zoho Books configuration
                        </div>
                      )}

                      <form onSubmit={handleSaveZohoConfig}>
                        <div className="mb-3">
                          <label className="form-label">
                            Client ID <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="clientId"
                            value={zohoFormData.clientId}
                            onChange={handleZohoInputChange}
                            placeholder="1000.XXXXXXXXXXXXX"
                            required
                          />
                          <small className="form-text text-muted">Your Zoho API client ID</small>
                        </div>

                        <div className="mb-3">
                          <label className="form-label">
                            Client Secret{" "}
                            {!zohoConfig ? (
                              <span className="text-danger">*</span>
                            ) : (
                              <span className="text-muted">(optional - leave blank to keep current)</span>
                            )}
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            name="clientSecret"
                            value={zohoFormData.clientSecret}
                            onChange={handleZohoInputChange}
                            placeholder={zohoConfig ? "Leave blank to keep current" : "Enter client secret"}
                            required={!zohoConfig}
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">
                            Refresh Token{" "}
                            {!zohoConfig ? (
                              <span className="text-danger">*</span>
                            ) : (
                              <span className="text-muted">(optional - leave blank to keep current)</span>
                            )}
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            name="refreshToken"
                            value={zohoFormData.refreshToken}
                            onChange={handleZohoInputChange}
                            placeholder={zohoConfig ? "Leave blank to keep current" : "1000.XXXXXXXXXXXXX"}
                            required={!zohoConfig}
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">
                            Organization ID <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="organizationId"
                            value={zohoFormData.organizationId}
                            onChange={handleZohoInputChange}
                            placeholder="Enter organization ID"
                            required
                          />
                        </div>

                        <div className="alert alert-warning mb-3" role="alert">
                          <i className="ti ti-alert-triangle me-2"></i>
                          <small>
                            <strong>Note:</strong> You can get these credentials from your{" "}
                            <a
                              href="https://api-console.zoho.com/"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Zoho API Console
                            </a>
                          </small>
                        </div>

                        <div className="d-flex gap-2">
                          <button type="submit" className="btn btn-success flex-fill" disabled={zohoSaving}>
                            {zohoSaving ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <i className="ti ti-device-floppy me-1"></i>Save
                              </>
                            )}
                          </button>
                          {showZohoForm && zohoConfig && (
                            <button
                              type="button"
                              className="btn btn-light"
                              onClick={() => setShowZohoForm(false)}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </form>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          </div>
          <div className="col-xl-5 col-lg-4 col-md-12 mb-4">
            <div className="card h-100">
              <div className="card-body">
                <h6 className="mb-3"><i className="ti ti-info-circle me-1"></i>How this works</h6>
                <ul className="small text-muted ps-3 mb-3">
                  <li className="mb-2">PayServe uses your Zoho <strong>refresh token</strong> to get short-lived access tokens automatically. Access tokens expire after an hour and are renewed for you.</li>
                  <li className="mb-2"><strong>Refresh credentials</strong> forces a new access token now. If it fails, the refresh token was revoked and you need to Edit and enter new credentials.</li>
                  <li className="mb-2"><strong>Item mapping</strong> decides which Zoho item (and so which income account) each kind of invoice posts to.</li>
                  {isNorthlands && <li><strong>Power &amp; Water sync</strong> sends bank payments, or monthly usage and top-ups, to Zoho as invoices and payments.</li>}
                </ul>
                {zohoConfig && (
                  <div className="small">
                    <div className="d-flex justify-content-between py-1 border-top"><span className="text-muted">Last token refresh</span><span>{zohoConfig.lastTokenRefreshAt ? new Date(zohoConfig.lastTokenRefreshAt).toLocaleString() : "Never"}</span></div>
                    <div className="d-flex justify-content-between py-1 border-top"><span className="text-muted">Token valid until</span><span>{zohoConfig.tokenExpiresAt ? new Date(zohoConfig.tokenExpiresAt).toLocaleString() : "Unknown"}</span></div>
                    <div className="d-flex justify-content-between py-1 border-top"><span className="text-muted">Last connection test</span><span>{zohoConfig.lastConnectionTest ? new Date(zohoConfig.lastConnectionTest).toLocaleString() : "Never"}</span></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "mapping" && connected && <ZohoItemMapping facilityId={facilityId} />}

      {tab === "sync" && connected && isNorthlands && (
        <div className="row">
          <div className="col-lg-6 col-md-12 mb-4">
            <ZohoUtilitySync facilityId={facilityId} utility="power" />
          </div>
          <div className="col-lg-6 col-md-12 mb-4">
            <ZohoUtilitySync facilityId={facilityId} utility="water" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ZohoIntegration;
